// ============================================================================
// ui/shell/step-tabs.js — centered GD folder-tab step row (reusable shell)
// ============================================================================
// Module-agnostic: knows nothing about the Weight Designer. Give it a list of
// step labels + an onSelect callback; it renders the centered folder tabs
// (`.ds-step-tabs` / `.ds-step-tab`) and exposes state setters. States mirror
// the Gym Designer: active / completed / disabled, plus a roving-tabindex
// keyboard model (←/→/Home/End) over the tablist.
//
//   const tabs = createStepTabs({ steps: ['Logo','Design','Product'], onSelect });
//   tabs.setActive(2);
// ============================================================================

import { h } from '../dom.js';

/**
 * @param {object} config
 * @param {Array<string|{label:string}>} config.steps
 * @param {(n:number)=>void} config.onSelect     - 1-based index of the clicked/activated tab.
 * @param {string} [config.ariaLabel]
 * @returns {{ el:HTMLElement, setActive:(n:number)=>void, setDisabled:(n:number,v:boolean)=>void,
 *            setCompleted:(n:number,v:boolean)=>void, getTab:(n:number)=>HTMLElement }}
 */
export function createStepTabs({ steps, onSelect, ariaLabel = 'Designer steps' }) {
  const els = steps.map((s, i) => {
    const label = typeof s === 'string' ? s : s.label;
    const n = i + 1;
    const tab = h(
      'button',
      {
        type: 'button',
        role: 'tab',
        class: 'ds-step-tab',
        'aria-selected': 'false',
        tabindex: '-1',
        onClick: () => {
          if (!tab.classList.contains('is-disabled')) onSelect(n);
        },
        onKeydown: (e) => onKeydown(e, i),
      },
      h('span', { class: 'ds-step-num', 'aria-hidden': 'true' }, String(n)),
      h('span', { class: 'ds-step-label' }, label),
    );
    return tab;
  });

  const el = h('div', { class: 'ds-step-tabs', role: 'tablist', 'aria-label': ariaLabel }, els);

  // Move focus AND the roving tabindex to `next` (skipping disabled tabs), so a
  // Tab-away/Tab-back returns to the tab the user arrowed to — not the originally
  // active one (WD-25). aria-selected still tracks the ACTIVE panel (set in
  // setActive), which is correct for the manual-activation tablist pattern.
  function focusTab(next) {
    els.forEach((t, k) => { t.tabIndex = k === next ? 0 : -1; });
    els[next].focus();
  }
  function stepIdx(from, dir) {
    const n = els.length;
    let idx = from;
    for (let k = 0; k < n; k++) {
      idx = (idx + dir + n) % n;
      if (!els[idx].classList.contains('is-disabled')) return idx;
    }
    return from;
  }
  function edgeIdx(dir) {
    // dir +1 → first enabled, -1 → last enabled
    return dir > 0 ? stepIdx(els.length - 1, 1) : stepIdx(0, -1);
  }
  function onKeydown(e, i) {
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = stepIdx(i, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = stepIdx(i, -1);
    else if (e.key === 'Home') next = edgeIdx(1);
    else if (e.key === 'End') next = edgeIdx(-1);
    if (next == null) return;
    e.preventDefault();
    if (els[next]) focusTab(next);
  }

  function setActive(n) {
    els.forEach((tab, i) => {
      const active = i + 1 === n;
      tab.classList.toggle('is-active', active);
      // ── THE STEPS YOU HAVE PASSED MARK THEMSELVES (Ryan 2026-07-28) ────────────────────────────
      // "tabs before the one you're on should include their number and a circle outline."
      //   `is-completed` has been styled in shell.css since this component was written and no caller
      // ever set it — `setCompleted` exists but nothing calls it. Deriving it here rather than asking
      // every consumer to is what stops the row from having two sources of truth about the same fact:
      // "before the active one" is not a judgement a caller has that this function doesn't.
      //   `setCompleted` stays on the public API for a consumer that wants to say something this
      // cannot infer (a step completed out of order); it simply is no longer the only way in.
      tab.classList.toggle('is-completed', i + 1 < n);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
  }
  function setDisabled(n, v) {
    const tab = els[n - 1];
    if (!tab) return;
    tab.classList.toggle('is-disabled', !!v);
    if (v) tab.setAttribute('aria-disabled', 'true');
    else tab.removeAttribute('aria-disabled');
  }
  function setCompleted(n, v) {
    const tab = els[n - 1];
    if (tab) tab.classList.toggle('is-completed', !!v);
  }

  return { el, setActive, setDisabled, setCompleted, getTab: (n) => els[n - 1] };
}
